/**
 * Safe ID scan alert banner — top of the dashboard.
 *
 * When someone scans a child's Safe ID emergency tag in the last 24 h, this
 * banner makes the parent see it the moment they open any dashboard page
 * (today the only surface that showed it was the buried Safe ID page).
 *
 * Real-time updates: subscribes to the family-events WebSocket. On a fresh
 * `safeid.scanned` event we (a) refetch the family-scans list, (b) toast
 * the parent, and (c) fire a browser Notification so the alert breaks
 * through even when this tab is not focused. Permission is requested
 * lazily on the first scan event so we don't pester users who never have
 * a scan happen.
 *
 * Auto-decays: scans older than 24 h drop out via the backend query
 * window — no manual ack needed for now.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { MapPin, QrCode, AlertOctagon, X } from 'lucide-react';

import { safeIdApi, type ScanEvent } from '@/api/safeId';
import { usersApi } from '@/api/users';
import { useFamilyEvents, type KidzioEvent } from '@/realtime/useFamilyEvents';

function relativeShort(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ago`;
  return `${Math.floor(h / 24)} d ago`;
}

/** Deep link to the dashboard's own Map page with a labeled pin at the
 *  approximate coords the finder shared. Map page reads ?lat/?lng/?focus
 *  and drops a rose-colored pin tied to the child's name in the tooltip. */
function mapsLink(lat: number, lng: number, childId: string): string {
  const params = new URLSearchParams({
    lat: String(lat),
    lng: String(lng),
    focus: childId,
    pin_label: '!',
    pin_color: '#f43f5e',
  });
  return `/map?${params.toString()}`;
}

const DISMISS_KEY = 'kidzio.safeid.dismissed-at';

export function SafeIdAlertBanner() {
  const qc = useQueryClient();

  // "Dismissed-up-to" timestamp lives in localStorage. The banner hides
  // any scan older-or-equal; if a NEW scan arrives after dismissal, the
  // banner reappears for it — dismiss is per-batch, not permanent.
  const [dismissedAt, setDismissedAt] = useState<number>(() => {
    if (typeof localStorage === 'undefined') return 0;
    const raw = localStorage.getItem(DISMISS_KEY);
    return raw ? Number(raw) || 0 : 0;
  });
  const dismissAll = (latest: string) => {
    const ts = new Date(latest).getTime();
    setDismissedAt(ts);
    try { localStorage.setItem(DISMISS_KEY, String(ts)); } catch { /* private mode */ }
  };

  const scans = useQuery({
    queryKey: ['safeid', 'family-scans'],
    queryFn: () => safeIdApi.familyScans(24),
    retry: false,
    refetchInterval: 60_000,
  });

  const children = useQuery({
    queryKey: ['children'],
    queryFn: () => usersApi.listChildren(),
    retry: false,
    staleTime: 60_000,
  });

  // Lazily ask for Notification permission only when the user has at least
  // one recent scan — no permission pop on a fresh, calm dashboard.
  const requestedPermissionRef = useRef(false);
  useEffect(() => {
    if (
      !requestedPermissionRef.current &&
      typeof Notification !== 'undefined' &&
      Notification.permission === 'default' &&
      (scans.data?.length ?? 0) > 0
    ) {
      requestedPermissionRef.current = true;
      void Notification.requestPermission();
    }
  }, [scans.data]);

  // Real-time WS handler — fires for *every* family event; we only react
  // to safeid.scanned.
  useFamilyEvents((evt: KidzioEvent) => {
    if (evt.type !== 'safeid.scanned') return;
    const childName =
      children.data?.find((c) => c.id === evt.data.child_id)?.name ?? 'A child';
    const body = evt.data.with_location
      ? `${childName}'s Safe ID was scanned. Finder shared their location.`
      : `${childName}'s Safe ID was just scanned.`;

    toast.warning('Safe ID scanned', { description: body, duration: 12_000 });

    if (
      typeof Notification !== 'undefined' &&
      Notification.permission === 'granted'
    ) {
      try {
        new Notification('Kidzio · Safe ID scanned', {
          body,
          icon: '/logo-mark.svg',
          tag: `safeid-${evt.data.token}`,
          requireInteraction: evt.data.with_location, // sticky when help is en route
        });
      } catch {
        /* notification spec: failures are non-fatal */
      }
    }

    void qc.invalidateQueries({ queryKey: ['safeid', 'family-scans'] });
  });

  // Apply the per-batch dismiss filter before deciding whether to render.
  const visible = (scans.data ?? []).filter(
    (s) => new Date(s.scanned_at).getTime() > dismissedAt,
  );
  if (visible.length === 0) return null;

  const childById = new Map((children.data ?? []).map((c) => [c.id, c.name]));
  const rows = visible.slice(0, 3);
  const latestVisible = visible[0]!.scanned_at; // visible is sorted desc by backend

  return (
    <div className="mx-auto mb-4 max-w-7xl rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3 shadow-sm sm:px-6">
      <div className="flex items-start gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-full bg-rose-100 text-rose-700 shadow-sm">
          <AlertOctagon className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-rose-900">
            {scans.data!.length === 1
              ? "Someone scanned a child's Safe ID tag"
              : `${scans.data!.length} Safe ID scans in the last 24 hours`}
          </p>
          <p className="text-xs text-rose-800/80">
            They may have found your child — review the details and reply if needed.
          </p>
          <ul className="mt-2 grid gap-1.5">
            {rows.map((s: ScanEvent) => {
              const name = childById.get(s.child_id) ?? 'Child';
              return (
                <li
                  key={s.id}
                  className="flex flex-wrap items-center gap-2 text-xs text-rose-900"
                >
                  <QrCode className="size-3.5 shrink-0 opacity-70" />
                  <span className="font-semibold">{name}</span>
                  <span className="opacity-70">·</span>
                  <span>{relativeShort(s.scanned_at)}</span>
                  {s.approx_lat != null && s.approx_lng != null && (
                    <>
                      <span className="opacity-70">·</span>
                      <Link
                        to={mapsLink(s.approx_lat, s.approx_lng, s.child_id)}
                        className="inline-flex items-center gap-1 font-semibold text-rose-700 underline-offset-2 hover:underline"
                      >
                        <MapPin className="size-3" />
                        View on map
                      </Link>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
          {(scans.data?.length ?? 0) > rows.length && (
            <p className="mt-1.5 text-[11px] text-rose-800/70">
              +{scans.data!.length - rows.length} more.
            </p>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <Link
            to="/safe-id"
            className="rounded-full bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-rose-700"
          >
            Open Safe ID
          </Link>
          <button
            type="button"
            onClick={() => dismissAll(latestVisible)}
            aria-label="Dismiss alert"
            title="Hide until the next scan"
            className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium text-rose-700/80 hover:bg-rose-100 hover:text-rose-900"
          >
            <X className="size-3" />
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}
